import { z } from 'zod';
import type { FormField } from '../types';
import { evaluateQuestionCondition, isQuestionConditionDraft, questionConditionIssue, type ConditionAnswer, type QuestionCondition, CONDITION_OPERATORS, type QuestionConditionSource } from './question-conditions';

/** Authored organisation metadata, never a respondent step or answer key. */
export interface ConversationSection {
  id: string;
  title: string;
  description?: string;
  /** Every member also requires this rule to match. */
  showWhen?: QuestionCondition;
  fieldIds: string[];
  /** Empty headings sit immediately before this field; absent means the end. */
  beforeFieldId?: string;
}

/** A bounded, strict shape shared by APIs, generation and stored configuration. */
export const conversationSectionSchema = z.object({
  id: z.string().trim().min(1).max(200).regex(/^[^<>\x00-\x1f\x7f]+$/),
  title: z.string().trim().min(1).max(200),
  description: z.string().max(4000).optional(),
  showWhen: z.object({ version: z.literal(1), match: z.enum(['all', 'any']),
    rules: z.array(z.object({ fieldId: z.string().min(1).max(200), operator: z.enum(CONDITION_OPERATORS),
      value: z.union([z.string().max(2000), z.number(), z.boolean()]).optional(),
      valueTo: z.union([z.string().max(2000), z.number()]).optional(), part: z.string().min(1).max(200).optional(),
    }).strict()).min(1).max(20),
  }).strict().refine(isQuestionConditionDraft).optional(),
  fieldIds: z.array(z.string().min(1).max(200)).max(200).refine(ids => new Set(ids).size === ids.length),
  beforeFieldId: z.string().min(1).max(200).optional(),
}).strict();

/** Metadata issues stay separate from question validation and question limits. */
export interface ConversationSectionIssue { sectionId: string; index: number; reason: string; message: string }

/** Renderable contiguous blocks preserve the canonical question sequence. */
export type ConversationSectionBlock =
  | { kind: 'section'; section: ConversationSection; fields: FormField[]; startIndex: number }
  | { kind: 'ungrouped'; fields: FormField[]; startIndex: number };

/** Validate membership against the authoritative field order at write boundaries. */
export function validateConversationSections(fields: readonly FormField[], sections?: readonly ConversationSection[]): ConversationSectionIssue[] {
  if (!sections) return [];
  const issues: ConversationSectionIssue[] = [];
  const positions = new Map(fields.map((field, index) => [field.id, index]));
  const owners = new Map<string, string>();
  const ids = new Set<string>();
  const ranges: { start: number; end: number }[] = [];
  let previousEnd = 0;
  const add = (section: ConversationSection, index: number, reason: string, message: string) => issues.push({ sectionId: section.id, index, reason, message });
  if (sections.length > 200) return [{ sectionId: '', index: 200, reason: 'too_many_sections', message: 'Use at most 200 sections.' }];
  sections.forEach((section, index) => {
    if (!conversationSectionSchema.safeParse(section).success) add(section, index, 'invalid_section', 'Check the section title and membership.');
    if (positions.has(section.id)) add(section, index, 'duplicate_section_id', 'Section and question identities must differ.');
    if (section.showWhen !== undefined) {
      const issue = questionConditionIssue(section.showWhen, sectionConditionSources(fields, sections, section.id));
      if (issue) add(section, index, 'invalid_condition', issue);
    }
    if (ids.has(section.id)) add(section, index, 'duplicate_section_id', 'Each section needs a unique identity.');
    ids.add(section.id);
    const positionsInSection = section.fieldIds.map(id => positions.get(id));
    for (const id of section.fieldIds) {
      if (!positions.has(id)) add(section, index, 'unknown_field', 'A section references a question that no longer exists.');
      if (owners.has(id)) add(section, index, 'duplicate_membership', 'A question can belong to only one section.');
      owners.set(id, section.id);
    }
    if (section.fieldIds.length && section.beforeFieldId !== undefined) add(section, index, 'unexpected_empty_anchor', 'Only empty sections use a position anchor.');
    if (section.beforeFieldId !== undefined && !positions.has(section.beforeFieldId)) add(section, index, 'unknown_anchor', 'The empty section position no longer exists.');
    const start = positionsInSection[0] ?? (section.beforeFieldId ? positions.get(section.beforeFieldId) : fields.length) ?? fields.length;
    if (positionsInSection.some((position, offset) => position !== start + offset)) add(section, index, 'noncontiguous_membership', 'Keep the questions in a section together and in conversation order.');
    if (start < previousEnd) add(section, index, 'section_order', 'Keep sections in conversation order.');
    previousEnd = start + section.fieldIds.length;
    if (section.fieldIds.length) ranges.push({ start, end: previousEnd });
  });
  sections.forEach((section, index) => {
    if (section.fieldIds.length) return;
    const anchor = section.beforeFieldId ? positions.get(section.beforeFieldId) : fields.length;
    if (anchor !== undefined && (ranges.some(range => anchor > range.start && anchor < range.end) || fields[anchor]?.informationFieldId)) add(section, index, 'invalid_empty_boundary', 'Place empty sections between complete question groups.');
  });
  for (const field of fields) {
    if (field.informationFieldId && owners.get(field.id) !== owners.get(field.informationFieldId)) {
      const owner = owners.get(field.id) ?? owners.get(field.informationFieldId);
      const index = sections.findIndex(section => section.id === owner);
      if (index >= 0) add(sections[index], index, 'split_information_group', 'Keep Information and its attached question in the same section.');
    }
  }
  return issues;
}

/** Clean stale draft references without reordering or replacing any questions. */
export function normalizeConversationSections(fields: readonly FormField[], sections?: readonly ConversationSection[]): ConversationSection[] {
  if (!sections) return [];
  const positions = new Map(fields.map((field, index) => [field.id, index]));
  const ids = new Set<string>();
  const owner = new Map<string, string>();
  const unique = sections.filter(section => { if (ids.has(section.id)) return false; ids.add(section.id); return true; });
  for (const section of unique) for (const id of section.fieldIds) if (positions.has(id) && !owner.has(id)) owner.set(id, section.id);
  for (const field of fields) {
    if (!field.informationFieldId) continue;
    const sectionId = owner.get(field.informationFieldId) ?? owner.get(field.id);
    if (sectionId) { owner.set(field.id, sectionId); owner.set(field.informationFieldId, sectionId); }
  }
  return unique.map(section => {
    const { beforeFieldId, ...metadata } = section;
    const fieldIds = fields.filter(field => owner.get(field.id) === section.id).map(field => field.id);
    return { ...metadata, fieldIds, ...(!fieldIds.length && beforeFieldId && positions.has(beforeFieldId) ? { beforeFieldId } : {}) };
  }).sort((a, b) => {
    const start = (section: ConversationSection) => positions.get(section.fieldIds[0] ?? section.beforeFieldId ?? '') ?? fields.length;
    return start(a) - start(b) || Number(a.fieldIds.length > 0) - Number(b.fieldIds.length > 0);
  });
}

/** Project every field once; ungrouped fields retain their place between sections. */
export function getConversationSectionBlocks(fields: readonly FormField[], sections?: readonly ConversationSection[]): ConversationSectionBlock[] {
  const normalized = normalizeConversationSections(fields, sections);
  const positions = new Map(fields.map((field, index) => [field.id, index]));
  const owners = new Map(normalized.flatMap(section => section.fieldIds.map(id => [id, section] as const)));
  const empty = new Map<number, ConversationSection[]>();
  for (const section of normalized) if (!section.fieldIds.length) {
    const position = positions.get(section.beforeFieldId ?? '') ?? fields.length;
    empty.set(position, [...(empty.get(position) ?? []), section]);
  }
  const blocks: ConversationSectionBlock[] = [];
  let index = 0;
  while (index <= fields.length) {
    for (const section of empty.get(index) ?? []) blocks.push({ kind: 'section', section, fields: [], startIndex: index });
    if (index === fields.length) break;
    const startIndex = index;
    const section = owners.get(fields[index].id);
    const group: FormField[] = [fields[index++]];
    while (index < fields.length && !empty.has(index) && owners.get(fields[index].id) === section) group.push(fields[index++]);
    blocks.push(section ? { kind: 'section', section, fields: group, startIndex } : { kind: 'ungrouped', fields: group, startIndex });
  }
  return blocks;
}

/** Clone metadata and its references with fresh identities without changing answer keys. */
export function cloneConversationSections(sections: readonly ConversationSection[], sourceFields: readonly FormField[], clonedFields: readonly FormField[]): ConversationSection[] {
  const ids = new Map(sourceFields.map((field, index) => [field.id, clonedFields[index]?.id]));
  const sectionIds = new Map(sections.map(section => [section.id, crypto.randomUUID()]));
  const optionIds = new Map<string, string>();
  const matrixRows = new Map<string, string>();
  const matrixColumns = new Map<string, string>();
  for (const [index, source] of sourceFields.entries()) {
    const cloned = clonedFields[index];
    if ('options' in source && cloned && 'options' in cloned) source.options.forEach((option, position) => {
      if (cloned.options[position]) optionIds.set(`${source.id}:${option.id}`, cloned.options[position].id);
    });
    if (source.type === 'matrix' && cloned?.type === 'matrix') for (const axis of ['rows', 'columns'] as const) source.matrix[axis].forEach((item, position) => {
      if (cloned.matrix[axis][position]) (axis === 'rows' ? matrixRows : matrixColumns).set(`${source.id}:${item.id}`, cloned.matrix[axis][position].id);
    });
  }
  return sections.map(section => ({ ...structuredClone(section), id: sectionIds.get(section.id)!,
    fieldIds: section.fieldIds.flatMap(id => ids.get(id) ? [ids.get(id)!] : []),
    ...(section.beforeFieldId ? { beforeFieldId: ids.get(section.beforeFieldId) } : {}),
    ...(section.showWhen ? { showWhen: { ...section.showWhen, rules: section.showWhen.rules.map(rule => ({ ...rule,
      fieldId: ids.get(rule.fieldId) ?? sectionIds.get(rule.fieldId) ?? rule.fieldId,
      ...(typeof rule.value === 'string' ? { value: matrixColumns.get(`${rule.fieldId}:${rule.value}`) ?? optionIds.get(`${rule.fieldId}:${rule.value}`) ?? rule.value } : {}),
      ...(rule.part ? { part: matrixRows.get(`${rule.fieldId}:${rule.part}`) ?? optionIds.get(`${rule.fieldId}:${rule.part}`) ?? rule.part } : {}),
    })) } } : {}),
  }));
}

/** Only facts preceding the entire section can gate its members. */
export function sectionConditionSources(fields: readonly FormField[], sections: readonly ConversationSection[], sectionId: string): QuestionConditionSource[] {
  const index = sections.findIndex(section => section.id === sectionId);
  const section = sections[index];
  if (!section) return [];
  const start = fields.findIndex(field => field.id === (section.fieldIds[0] ?? section.beforeFieldId));
  const boundary = start < 0 ? fields.length : start;
  const earlier = sections.slice(0, index).filter(candidate => candidate.fieldIds.every(id => fields.findIndex(field => field.id === id) < boundary));
  return [...fields.slice(0, boundary), ...earlier.map(candidate => ({ id: candidate.id, name: candidate.id, label: candidate.title, type: 'section' }))];
}

/** Section presence describes its selected path, including an explicit skipped branch. */
export function sectionConditionFacts(sections: readonly ConversationSection[], answers: Readonly<Record<string, ConditionAnswer>>): Record<string, ConditionAnswer> {
  const facts: Record<string, ConditionAnswer> = { ...answers };
  for (const section of sections) {
    const eligible = evaluateQuestionCondition(section.showWhen, facts);
    facts[section.id] = { status: eligible === 'pending' ? 'pending' : eligible ? 'answered' : 'unanswered' };
  }
  return facts;
}

/** Resolve the section gate independently of a member's own all/any rule group. */
export function fieldSectionEligibility(fieldId: string, sections: readonly ConversationSection[], answers: Readonly<Record<string, ConditionAnswer>>): boolean | 'pending' {
  const section = sections.find(candidate => candidate.fieldIds.includes(fieldId));
  if (!section) return true;
  const status = sectionConditionFacts(sections, answers)[section.id]?.status;
  return status === 'pending' ? 'pending' : status === 'answered';
}
